import { Component, forwardRef, Inject, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { ErrorMessages, Permissions } from "../../common/constants";
import { Properties } from "../../common/properties";
import { WorkSessionService } from "../../dashboard-area/shared/work-session.service";
import { Algorithm, AlgorithmOutputType, Metric, MetricValueType, WorkSession, WorkSessionDefinition } from "../../model";
import { AlgorithmService } from "../../service/algorithm.service";
import { AuthenticationService } from "../../service/authentication.service";
import { ThingService } from "../../service/thing.service";
import { WorkSessionDefinitionService } from "../../service/work-session-definition.service";
import { PropertyComponent, PropertyEditorType } from "../../shared/component";
import { CustomPropertyFormComponent } from "../../shared/custom-property/custom-property-form.component";
import { ErrorUtility } from "../../utility/error-utility";
import { AddTicketDialogComponent } from "../ticket-list/add-ticket-dialog.component";

@Component({
    selector: 'work-session-edit-dialog',
    template: require('./work-session-edit-dialog.component.html'),
})
export class WorkSessionEditDialogComponent {

    @ViewChild('form') form: NgForm;

    @ViewChild('customPropForm') customPropForm: CustomPropertyFormComponent;

    writePermission: boolean;
    error: string;
    inputs: Input[];
    workSession: WorkSession;
    updating: boolean;
    workSessionDefinition: WorkSessionDefinition;
    loaded: boolean;
    visiblePropertyNames: string[];

    private algorithmsById: { [id: string]: Algorithm } = {};

    constructor(
        @Inject(forwardRef(() => MatDialogRef)) private dialogRef: MatDialogRef<AddTicketDialogComponent>,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => WorkSessionDefinitionService)) private workSessionDefinitionService: WorkSessionDefinitionService,
        @Inject(forwardRef(() => AlgorithmService)) private algorithmService: AlgorithmService,
        @Inject(forwardRef(() => WorkSessionService)) private workSessionService: WorkSessionService,
        @Inject(forwardRef(() => ThingService)) private thingService: ThingService,
        @Inject(MAT_DIALOG_DATA) data
    ) {
        this.writePermission = this.authenticationService.hasPermission(Permissions.WRITE_WORK_SESSION);
        this.workSession = data.workSession;
        this.workSessionDefinitionService.getWorkSessionDefinitionsById(this.workSession.workSessionDefinitionId).then(wsDef => {
            this.workSessionDefinition = wsDef;
            let algorithmPromises = [];
            this.getAlgorithmIds(data.properties).forEach(id => {
                algorithmPromises.push(this.algorithmService.getAlgorithmById(id).then(al => this.algorithmsById[al.id] = al));
            });
            Promise.all(algorithmPromises).then(() => {
                this.initInputs(data.properties);
                this.loaded = true;
            });
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private getAlgorithmIds(properties: PropertyComponent[]): string[] {
        let results = [];
        properties?.forEach(p => {
            if (p.name.startsWith("postComputationResults.") && p.name.split('.').length == 3) {
                const algorithmId = this.workSessionDefinition.postComputationAlgorithms?.find(al => al.resultField == p.name.split('.')[1])?.algorithmId;
                if (algorithmId && !results.includes(algorithmId)) {
                    results.push(algorithmId);
                }
            }
        });
        return results;
    }

    private initInputs(properties: PropertyComponent[]): void {
        this.inputs = [];
        this.visiblePropertyNames = [];
        properties.forEach(p => {
            if (p.name == 'startTimestamp' || p.name == 'endTimestamp') {
                this.inputs.push({ name: p.name, label: p.label || Properties.WorkSession[p.name]?.label || p.name, editor: PropertyEditorType.DATE_TIME, type: MetricValueType.LONG, defaultValue: this.workSession[p.name], unit: null })
            } else if (p.name.startsWith("metrics.")) {
                this.handleMonitoredMetric(p);
            } else if (p.name.startsWith("postComputationResults.")) {
                this.handlePostComputation(p);
            } else if (p.name.startsWith("properties.")) {
                this.visiblePropertyNames.push(p.name.substring(11));
            }
        });
    }

    private handleMonitoredMetric(property: PropertyComponent): void {
        const nameParts = property.name.split('.');
        if (nameParts.length == 3) {
            const metric: Metric = this.workSessionDefinition.monitoredMetrics.find(m => m.name == nameParts[1]);
            if (metric) {
                const defaultValue = this.workSession[nameParts[0]] != null && this.workSession[nameParts[0]][nameParts[1]] != null ? this.workSession[nameParts[0]][nameParts[1]][nameParts[2]] : null
                this.inputs.push({ name: property.name, label: property.label || metric.label || metric.name, editor: property.editor, type: metric.valueType, defaultValue: defaultValue, unit: metric.unit });
            }
        }
    }

    private handlePostComputation(property: PropertyComponent): void {
        const nameParts = property.name.split('.');
        if (nameParts.length == 3) {
            const algorithmId = this.workSessionDefinition.postComputationAlgorithms?.find(al => al.resultField == nameParts[1])?.algorithmId;
            const algorithm = this.algorithmsById[algorithmId];
            if (algorithm) {
                const output = algorithm.outputs?.find(output => output.name == nameParts[2]);
                if (output) {
                    const defaultValue = this.workSession[nameParts[0]] != null && this.workSession[nameParts[0]][nameParts[1]] != null ? this.workSession[nameParts[0]][nameParts[1]][nameParts[2]] : null
                    this.inputs.push({ name: property.name, label: property.label || output.label || output.name, editor: property.editor, type: output.type, defaultValue: defaultValue, unit: output.unit });
                }
            }
        }
    }

    save(): void {
        let values = this.form.form.getRawValue();
        values.properties = Object.assign({}, this.workSession.properties, this.customPropForm?.getProperties());
        this.updating = true;
        let body = {};
        if (Object.keys(values).some(key => key.startsWith('metrics.'))) {
            Object.assign(body, { metrics: this.workSession.metrics });
        }
        if (Object.keys(values).some(key => key.startsWith('postComputationResults.'))) {
            Object.assign(body, { postComputationResults: this.workSession.postComputationResults });
        }
        Object.entries(values).forEach(entry => {
            if (entry[0].startsWith('metrics.') || entry[0].startsWith('postComputationResults.')) {
                const nameParts = entry[0].split('.');
                if (body[nameParts[0]] != null) {
                    if (body[nameParts[0]][nameParts[1]] != null) {
                        body[nameParts[0]][nameParts[1]][nameParts[2]] = entry[1];
                    } else {
                        body[nameParts[0]][nameParts[1]] = {};
                        body[nameParts[0]][nameParts[1]][nameParts[2]] = entry[1];
                    }
                } else {
                    body[nameParts[0]] = {};
                    body[nameParts[0]][nameParts[1]] = {};
                    body[nameParts[0]][nameParts[1]][nameParts[2]] = entry[1];
                }
            } else {
                body[entry[0]] = entry[1];
            }
        });
        this.workSessionService.patchHistoricalWorkSession(this.workSession.id, body).then(() => {
            this.thingService.getHistoricalWorkSessionById(this.workSession.id).then(ws => {
                this.dialogRef.close(ws);
            });
        }).catch(err => {
            this.updating = false;
            this.error = ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR);
        });
    }

    isCustomPropertiesFormValid(): boolean {
        if (!this.customPropForm) {
            return true;
        }
        return this.customPropForm.checkForm();
    }
}

class Input {
    name: string;
    label: string;
    editor: PropertyEditorType;
    type: MetricValueType | AlgorithmOutputType;
    defaultValue: any;
    unit: string;
}